import { faCircle } from '@fortawesome/pro-regular-svg-icons/faCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@mui/material';
import { withStyles, WithStyles } from "@mui/styles";
import * as pubApi from "api/Knowledge";
import styles from "assets/jss/cardCreation.styles";
import classNames from "classnames";
import { AppMessageBox } from 'components/AppMessageBox';
import IconButtonClose from 'components/Buttons/IconButtonClose';
import IconButtonDelete from 'components/Buttons/IconButtonDelete';
import { RequestIndicator } from 'components/CallServer';
import RequestErrorDialog from 'components/CallServer/RequestErrorDialog';
import { RequestErrorReport } from 'components/CallServer/RequestErrorReport';
import MuiRegularButton from 'components/MuiExtras/Button/MuiRegularButton';
import MuiContainer from 'components/MuiExtras/MuiContainer';
import MuiKeyboardDatePicker from 'components/MuiExtras/MuiKeyboardDatePicker';
import MuiKeyboardTimePicker from 'components/MuiExtras/MuiKeyboardTimePicker';
import MuiWarning from 'components/MuiExtras/Typography/MuiWarning';
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import AppMessage from 'components/Translations/AppMessage';
import { PublicationEditPermissions, SystemRoles } from 'models/ApiEnums';
import { GetPollDto, PollManager, SavePollDto, SavePublicationResult } from 'models/ApiModels';
import { RequestLifeCycle } from 'models/AppModels';
import moment from "moment";
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import Session from 'services/Session';
import TopicsAndMembersBox from 'views/Publication/Components/TopicsAndMembersBox';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId?: string;
    requestAnswerFromUser?: string;
    onClose: (result: SavePublicationResult) => void;
}

interface IValidation {
    title?: boolean;
    topics?: boolean;
    options?: boolean;
    dateUntil?: boolean;
    timeUntil?: boolean;
}

interface IState {
    record: Partial<GetPollDto>;
    pollManager: PollManager;
    validation: IValidation;
    dateUntil?: moment.Moment;
    timeUntil?: moment.Moment;

    step: number;

    formCycle: RequestLifeCycle;
    lastError: any;
}

interface IDispatchProps {
}

type Props = IProps & IDispatchProps & WithStyles<typeof styles> & WithTranslation & WithSnackbar;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class CreatePoll extends React.Component<Props, IState> {
    private refTitle: HTMLTextAreaElement;
    private timerLastChange: number;
    private timer: NodeJS.Timeout;

    constructor(props: Props) {
        super(props);

        this.state = {
            record: {
                publicationId: null,
                answers: null,
                allowAnswersUntil: null,
                contentBody: "",
                title: "",
                topics: [],
                relativesTopics: [],
                deletedRelativesTopics: [],
                editPermissions: PublicationEditPermissions.Full
            },
            validation: {
                title: null,
                topics: null,
                options: null,
                dateUntil: true,
                timeUntil: true
            },
            timeUntil: null,
            dateUntil: null,

            pollManager: new PollManager(null),

            step: 1,

            formCycle: RequestLifeCycle.None,
            lastError: null
        };
    }

    ///
    private updateRecord(data: Partial<GetPollDto>) {
        return { ...this.state.record, ...data };
    }

    ///
    private handleTitleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        var v = event.target.value;
        var validation = this.state.validation;

        validation.title = v.length > 0;
        this.setState({
            record: this.updateRecord({ title: v }),
            validation
        });
    };

    ///
    private handleTitleKeyUp = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {

        if (this.timer) clearTimeout(this.timer);

        this.timerLastChange = new Date().getTime();

        this.timer = setTimeout(() => {
            if ((new Date().getTime() - this.timerLastChange) > 200) {
                //  Adiciona "?" no final do texto.
                if (this.refTitle.value.length > 0 && this.refTitle.value[this.refTitle.value.length - 1] != "?") {
                    this.refTitle.value += "?";
                    this.setState({
                        record: this.updateRecord({ title: this.refTitle.value }),
                    }, () => this.handleTitleCaret());
                }
            }
        }, 200);
    };

    ///
    private handleTitleCaret = () => {
        // Reposiciona CARET antes do "?" do final do texto.
        if (this.refTitle) {
            if (this.refTitle.selectionStart == this.refTitle.selectionEnd) { // Garante que não está selecionando um texto
                if (this.refTitle.value.length > 1 && this.refTitle.selectionEnd > this.refTitle.value.length - 1) {
                    this.refTitle.selectionStart = this.refTitle.selectionEnd = this.refTitle.value.length - 1;
                }
            }
        }
    };

    ///
    private handleAddOption = () => {
        const { pollManager } = this.state;

        if (pollManager.answers.length < 5) {
            pollManager.addAnswer("");
            this.setState({ pollManager });
        }
    };

    ///
    private handleDelete(index: number) {
        const { pollManager } = this.state;
        if (pollManager.answers.length > 2) {
            pollManager.answers.splice(index, 1);
            this.setState({ pollManager });
        }
    }

    ///
    private canSave(): boolean {
        const { validation, pollManager } = this.state;
        return validation.title && validation.topics && validation.timeUntil && pollManager.isValid;
    }

    ///
    private load(publicationId: string) {
        this.setState({ formCycle: RequestLifeCycle.Loading });
        pubApi.getPoll(publicationId)
            .then((record) => {

                this.setState({
                    formCycle: RequestLifeCycle.None,
                    record,
                    dateUntil: record.allowAnswersUntil == null ? null : moment(record.allowAnswersUntil),
                    timeUntil: record.allowAnswersUntil == null ? null : moment(record.allowAnswersUntil),
                    step: record.editPermissions == PublicationEditPermissions.None ? 0 : 1,
                    pollManager: new PollManager(record.answers),
                    validation: {
                        topics: true,
                        title: true,
                        options: true,
                        dateUntil: true,
                        timeUntil: true,
                    }
                });
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.LoadFailed });
            });
    }

    ///
    private save() {
        const { record, pollManager, dateUntil, timeUntil } = this.state;

        record.allowAnswersUntil = null;
        if (dateUntil != null) {
            record.allowAnswersUntil = dateUntil.toDate();
            record.allowAnswersUntil.setHours(timeUntil == null ? 23 : timeUntil.hours());
            record.allowAnswersUntil.setMinutes(timeUntil == null ? 59 : timeUntil.minutes());
            record.allowAnswersUntil.setSeconds(timeUntil == null ? 59 : timeUntil.seconds());
        }

        const dto: SavePollDto = {
            publicationId: record.publicationId,
            title: record.title,
            contentBody: record.contentBody,

            answers: pollManager.answers,
            allowAnswersUntil: record.allowAnswersUntil,

            topics: record.topics.filter(p => !p.fixed).map((v) => { return v.key; }),
            relativesTopics: record.relativesTopics?.map((v) => { return v.key; }),
        };

        this.setState({ formCycle: RequestLifeCycle.Posting });

        pubApi.savePoll(dto)
            .then((result) => {
                this.props.onClose(result);
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private loadPrediction() {
        if (this.state.step != 2) return;

        const { title, contentBody, publicationId, topics } = this.state.record;
        if (publicationId != null || topics?.length > 0) return;

        this.setState({ formCycle: RequestLifeCycle.Loading },
            () => {
                pubApi.getTopicPredictions(title, contentBody)
                    .then((result) => {
                        if (result) {
                            this.setState({
                                formCycle: RequestLifeCycle.None,
                                record: this.updateRecord({ topics: result.topics, relativesTopics: result.relativesTopics, deletedRelativesTopics: [] }),
                                validation: { ...this.state.validation, topics: true }
                            }, () => result?.topics.length > 0 && this.props.snackbar.showMessage(this.props.t("Views.Publication.Common.Form.TopicSuggestionFound"), "info"));
                        }
                        else {
                            this.setState({
                                formCycle: RequestLifeCycle.None
                            });
                        }
                    })
                    .catch((error) => {
                        this.setState({
                            formCycle: RequestLifeCycle.None
                        });
                    });
            });
    }

    ///
    private goStep1() {
        this.setState({ step: 1 });
    }

    ///
    private goStep2() {
        const { record, validation } = this.state;
        let step = this.state.step;

        const v: IValidation = {
            title: record.title?.length > 0,
            options: this.state.pollManager.isValid,
            dateUntil: validation.dateUntil,
            timeUntil: validation.timeUntil
        };

        if (v.title && v.options && v.dateUntil && v.timeUntil) step++;

        this.setState({
            step: step,
            validation: { ...validation, ...v }
        }, () => this.loadPrediction());
    }

    ///
    private canGoStep2(): boolean {
        const { record: { title }, validation: { dateUntil, timeUntil }, pollManager } = this.state;
        return title?.length > 0 && pollManager.isValid && dateUntil && timeUntil;
    }

    ///
    private step1() {
        const { classes } = this.props;
        const { record, pollManager, validation, dateUntil, timeUntil } = this.state;
        const { hasAnswers, editPermissions } = record;
        const { allowAnswersUntil } = record;

        const endOfDay = record.allowAnswersUntil != null && record.allowAnswersUntil.getHours() == 23 && record.allowAnswersUntil.getMinutes() == 59 && record.allowAnswersUntil.getSeconds() == 59;

        const autoFocusIndex = record.title == "" ? -1 : pollManager.answers.findIndex(p => p.text.length == 0);

        const readOnly = hasAnswers || editPermissions != PublicationEditPermissions.Full;

        return (
            <>
                <DialogContent className={classes.dialogContent}>

                    <div className={classes.container}>

                        <div className={classes.containerData} >

                            <MuiContainer>
                                <Grid container spacing={3} alignContent="center" >
                                    <Grid item>
                                        <Avatar className={this.props.classes.avatarLarge} src={Session.thumbnailUrl} />
                                    </Grid>
                                    <Grid item style={{ flexGrow: 1 }}>
                                        <div
                                            className={classNames(
                                                classes.containerData_textarea,
                                                { ["" + classes.fieldError]: validation.title === false })
                                            }
                                        >
                                            <textarea
                                                name="title"
                                                placeholder={this.props.t("Views.Publication.CreatePoll.TitlePlaceholder")}
                                                autoComplete="off"
                                                maxLength={240}
                                                style={{ fontSize: "1.8em" }}
                                                value={record.title}
                                                autoFocus
                                                rows={3}
                                                ref={(ref) => { this.refTitle = ref; }}
                                                readOnly={readOnly}
                                                onKeyUp={this.handleTitleKeyUp}
                                                onChange={this.handleTitleChange}
                                            />
                                        </div>

                                    </Grid>
                                </Grid>
                            </MuiContainer>

                            <MuiContainer>
                                {
                                    pollManager.answers.map((v, index) => {
                                        return (
                                            <Grid container alignContent="center" alignItems="center" spacing={1}>
                                                <Grid item>
                                                    <FontAwesomeIcon icon={faCircle} />
                                                </Grid>
                                                <Grid xs item>
                                                    <TextField
                                                        id={pollManager.answers[index].id}
                                                        fullWidth
                                                        autoFocus={index == autoFocusIndex}
                                                        autoComplete="off"
                                                        placeholder={this.props.t("Views.Publication.CreatePoll.OptionPlaceholder")}
                                                        error={validation.options === false && pollManager.answers[index].text.length == 0}
                                                        value={pollManager.answers[index].text}
                                                        disabled={readOnly}
                                                        onChange={
                                                            (e) => {
                                                                pollManager.answers[index].text = e.currentTarget.value;
                                                                this.setState({ pollManager });
                                                            }
                                                        }
                                                        onKeyDown={
                                                            (e) => {
                                                                if (e.keyCode == 13) {
                                                                    if (index < pollManager.answers.length - 1) {
                                                                        const el = document.getElementById(pollManager.answers[index + 1].id);
                                                                        if (el) el.focus();
                                                                    }
                                                                    else if (pollManager.answers[index].text.length > 0 && pollManager.answers.length < 5) {
                                                                        this.handleAddOption();
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    />
                                                </Grid>
                                                {
                                                    pollManager.answers.length > 2 && !readOnly &&
                                                    <Grid item>
                                                        <IconButtonDelete size="small" onClick={() => this.handleDelete(index)} />
                                                    </Grid>
                                                }
                                            </Grid>
                                        );
                                    })
                                }
                            </MuiContainer>

                            <MuiContainer>
                                {
                                    pollManager.answers.length < 5 && !readOnly &&
                                    <p>
                                        <MuiRegularButton
                                            link
                                            onClick={this.handleAddOption}
                                        >
                                            <span>{this.props.t("Views.Publication.CreatePoll.ButtonAddOption")}</span>
                                        </MuiRegularButton>
                                    </p>
                                }
                            </MuiContainer>

                            <MuiContainer>
                                <Grid container justifyContent="space-between" alignItems="flex-end">
                                    <Grid item>
                                        <MuiKeyboardDatePicker
                                            fullWidth
                                            clearable
                                            disabled={editPermissions != PublicationEditPermissions.Full}
                                            label={this.props.t<string>("Views.Publication.CreatePoll.VoteAvailableUntilLabel")}
                                            placeholder={this.props.t("Views.Publication.CreatePoll.VoteAvailableUntilPlaceholder")}
                                            minDate={moment('1900-01-01')}
                                            error={validation.dateUntil === false}
                                            InputLabelProps={
                                                {
                                                    shrink: true
                                                }
                                            }
                                            value={dateUntil}
                                            onChange={
                                                (date, v) => {
                                                    this.setState({
                                                        dateUntil: date,
                                                        validation: {
                                                            ...validation,
                                                            dateUntil: date == null || date.isValid(),
                                                            timeUntil: date == null || timeUntil == null || timeUntil.isValid(),
                                                        },
                                                        record: this.updateRecord({ allowAnswersUntil })
                                                    });
                                                }
                                            }
                                        />
                                    </Grid>
                                    <Grid item>
                                        <MuiKeyboardTimePicker
                                            fullWidth
                                            clearable
                                            disabled={dateUntil == null || editPermissions != PublicationEditPermissions.Full}
                                            label={this.props.t<string>("Views.Publication.CreatePoll.VoteHourLabel")}
                                            placeholder={this.props.t("Views.Publication.CreatePoll.VoteHourPlaceholder")}
                                            value={endOfDay ? null : timeUntil}
                                            error={validation.timeUntil === false}
                                            InputLabelProps={
                                                {
                                                    shrink: true
                                                }
                                            }
                                            onChange={
                                                (date) => {
                                                    this.setState({
                                                        timeUntil: date,
                                                        validation: {
                                                            ...validation,
                                                            timeUntil: date == null || date.isValid(),
                                                        },
                                                        record: this.updateRecord({ allowAnswersUntil })
                                                    });
                                                }}
                                        />
                                    </Grid>

                                </Grid>
                            </MuiContainer>

                        </div>
                    </div>
                </DialogContent>
                <DialogActions>

                    <div className={classes.footerMessage}>
                        <b>{this.props.t("Views.Publication.CreatePoll.Footer1")}</b>
                        <span>&nbsp;&raquo;&nbsp;</span>
                        <span>{this.props.t("Views.Publication.Common.Form.Footer2")}</span>
                    </div>
                    <MuiRegularButton
                        variant="contained"
                        color="primary"
                        disabled={!this.canGoStep2()}
                        onClick={() => this.goStep2()}
                    >
                        <span>{this.props.t("Views.Publication.Common.Form.ButtonContinueText")}</span>
                    </MuiRegularButton>
                </DialogActions>
            </>
        );
    }

    ///
    private step2() {
        const { classes } = this.props;
        const { record } = this.state;
        return (
            <>
                <DialogContent>
                    <div className={classes.container}>

                        <TopicsAndMembersBox
                            skipTopicAccessFor={SystemRoles.PublicationManager}
                            topics={record.topics}
                            relativesTopics={record.relativesTopics}
                            deletedRelativesTopics={record.deletedRelativesTopics}
                            findRelativesTopics={record.publicationId == null}
                            onChange={
                                (topics, relativesTopics, deletedRelativesTopics) => {
                                    this.setState({
                                        record: this.updateRecord({ topics, relativesTopics, deletedRelativesTopics }),
                                        validation: { ...this.state.validation, ...{ topics: topics.length > 0 } }
                                    });
                                }
                            }
                        />

                    </div>

                </DialogContent>

                <DialogActions>
                    <div className={classes.actions}>

                        <MuiRegularButton variant="contained" onClick={() => this.goStep1()} >
                            <span>{this.props.t("Views.Publication.Common.Form.ButtonBackText")}</span>
                        </MuiRegularButton>

                        <MuiRegularButton
                            variant="contained"
                            color="success"
                            style={{ float: "right" }}
                            onClick={() => this.save()}
                            disabled={!this.canSave()}
                        >
                            <span>{this.props.t("Views.Publication.Common.Form.ButtonFinishText")}</span>
                        </MuiRegularButton>
                    </div>
                </DialogActions>
            </>
        );
    }

    ///
    public componentDidMount() {
        if (this.props.publicationId != null) {
            this.load(this.props.publicationId);
        }
    }

    ///
    public render() {
        const { formCycle, lastError, step } = this.state;
        const { hasAnswers } = this.state.record;

        return (
            <Dialog
                open={true}
                maxWidth={"md"}
                fullWidth
            >
                <DialogTitle>
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <Typography variant="h6" component="p">
                                {this.props.t(this.state.record.publicationId ? "Views.Publication.CreatePoll.TitleEdit" : "Views.Publication.CreatePoll.TitleNew")}
                            </Typography>
                            {
                                hasAnswers &&
                                <Typography variant="body2">
                                    <MuiWarning><small><AppMessage resource="PublicationEditPollRestricted" /></small></MuiWarning>
                                </Typography>
                            }
                        </Grid>
                        <Grid item>
                            <IconButtonClose
                                color="inherit"
                                size="small"
                                onClick={() => this.props.onClose(null)}
                            />
                        </Grid>
                    </Grid>
                </DialogTitle>

                {
                    formCycle == RequestLifeCycle.LoadFailed
                        ? (
                            <>
                                <DialogContent className={this.props.classes.dialogContent}>
                                    <RequestErrorReport error={lastError} />;
                                </DialogContent>
                                <DialogActions />
                            </>
                        )
                        : step == 0
                            ? (
                                <>
                                    <DialogContent className={this.props.classes.dialogContent}>
                                        <AppMessageBox resource="PublicationEditPermissionDenied" variant="warning" />
                                    </DialogContent>
                                    <DialogActions />
                                </>
                            )
                            : step == 1
                                ? this.step1()
                                : step == 2
                                    ? this.step2()
                                    : null
                }

                <RequestIndicator type="request" open={formCycle == RequestLifeCycle.Loading} />
                <RequestIndicator type="post" open={formCycle == RequestLifeCycle.Posting} />

                <RequestErrorDialog
                    open={formCycle == RequestLifeCycle.PostFailed}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

            </Dialog>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(styles)(CreatePoll)));